/**
 * 로그·이력 목 데이터.
 * 출처: 와이어프레임 `eventRows()`(1353행) · `sysLogView()` `logs`(2543행) · `AUDIT`(2539행).
 *
 * 🔴 계약은 **두 엔드포인트**로 가른다.
 *   - `GET /history` → `HistoryEntry` — 네 원장(audit · command · event · batch_record)의 **합성 뷰**
 *   - `GET /logs`    → `LogEntry`     — 앱 로그(level · source · message)
 *   화면의 로그 3탭(이벤트 · 시스템 · 감사)은 그 둘을 나눠 쓴다:
 *   이벤트 = `HistoryEntry(event_type='event' | 'batch_record')` · 감사 = `HistoryEntry(event_type='audit')` ·
 *   시스템 = `LogEntry`.
 */
import type { HistoryEntry, LogEntry } from '@ctcells/contract'

/** 이벤트 원장 — 작업 사건. 상시 작업 줄의 「▸ 이벤트 N」이 이것을 편다 */
/*
 * 🔴 **이벤트 원장의 `target` 은 접두어가 «붙어» 온다** — 서버가 직접 이어 붙인다
 *    (`app/services/history.py:79-88` — `module:<id>` · `batch:<id>`). 접두어를 뗀 `cent` 로
 *    두면 fixture 가 실물과 다른 모양이 되고, 화면이 접두어를 못 벗겨도 시험이 통과한다.
 *    **2026-09-20 에 실제로 그래서 `module:cent` 가 화면에 샜다.**
 * ⚠ 감사 원장(`AUDIT_HISTORY`)은 규칙이 다르다 — `audit_log.target` 자유 문자열을 그대로 싣는다.
 */
/*
 * 🔴 **`detail` 의 키는 서버가 원장마다 «고정»으로 만든다** — 아무 키나 오지 않는다
 *    (`app/services/history.py`):
 *      event   → `category` · `operation` · `error_code` · `severity`
 *      command → `operation` · `result` · `log_level`
 *      audit   → `action` + `audit_log.detail`(`reason` 은 CHECK 로 «강제»된다)
 *    🔴 **`message` 를 넣지 마라.** 서버가 그 키를 안 만든다 — 예전 fixture 에 있어서 화면이
 *       `detail.message` 를 읽었고, 실물에서는 「내용」 열이 «항상» 비었다(2026-09-20 실측).
 *    ⚠ `correlation_id` 도 이 원장에는 안 실린다(앱 로그 `LogEntry.context` 쪽 이야기다).
 */
export const EVENT_HISTORY: HistoryEntry[] = [
  {
    ts: '2026-09-11T14:31:59+09:00',
    event_type: 'event',
    actor: null,
    target: 'module:cent',
    detail: {
      category: 'critical',
      operation: 'run_centrifugation',
      error_code: 'CENT_IMBALANCE',
      severity: 'CRITICAL',
    },
  },
  {
    ts: '2026-09-11T14:20:03+09:00',
    event_type: 'event',
    actor: null,
    target: 'module:cent',
    detail: {
      category: 'recoverable',
      operation: 'inspect',
      severity: 'INFO',
    },
  },
  {
    ts: '2026-09-11T14:19:55+09:00',
    event_type: 'event',
    actor: null,
    target: 'module:cent',
    detail: {
      category: 'recoverable',
      operation: 'run_centrifugation',
      severity: 'INFO',
    },
  },
  {
    ts: '2026-09-11T14:19:40+09:00',
    event_type: 'event',
    actor: null,
    target: 'module:robot',
    detail: {
      category: 'transient',
      error_code: 'COMM_TIMEOUT',
      severity: 'WARN',
    },
  },
  {
    ts: '2026-09-11T14:09:12+09:00',
    event_type: 'event',
    actor: null,
    target: 'module:cent',
    detail: {
      category: 'user_recoverable',
      error_code: 'LOAD_OVERDUE',
      severity: 'ERROR',
    },
  },
  /*
   * 🔴 **배치 축 이벤트**(`event_log.batch_id` 가 있는 행). 서버 `_event_select` 가
   *    `batch_id` 를 먼저 보고 `batch:<id>` 를 붙인다 — `module_id` 보다 **우선**이다.
   *    작업 상세 「이벤트」 탭이 이 접두어로 그 배치의 행만 고른다.
   */
  {
    ts: '2026-09-11T14:18:05+09:00',
    event_type: 'event',
    actor: null,
    target: 'batch:B-260911-02',
    detail: {
      category: 'recoverable',
      operation: 'start_batch',
      severity: 'INFO',
    },
  },
  {
    ts: '2026-09-11T14:09:20+09:00',
    event_type: 'event',
    actor: null,
    target: 'batch:B-260911-02',
    detail: {
      category: 'user_recoverable',
      operation: 'await_manual_load',
      error_code: 'LOAD_OVERDUE',
      severity: 'ERROR',
    },
  },
]

/**
 * 배치 기록 원장 — `GET /history?event_type=batch_record`(2026-10-08 · v0.9.4 · 정본 `05` `DM-REL-66`).
 *
 * 🔴 **배치 기록이 `event_log` 에서 떨어져 나왔다** — 발송(`dispatch_send`) · 실행 관측(`execution_observed`) ·
 *    진행 사본(`progress_record`) · 배치 전이(`batch_state_change`) · 멈춤 사유(`dispatch_halt`) · 재개 거부(`resume_refused`)가
 *    이제 여기 산다(`ctcells-core` `app/services/batch_records.py`). 모듈 상태 전이는 `event` 에 남는다.
 * 🔴 **모양은 서버 그대로**(`app/services/history.py` `_batch_record_select`):
 *    - `target` 은 늘 `batch:<id>`(배치에 늘 귀속된다) · `actor` 는 늘 null(서버가 키를 뺀다 — 여기선 키를 안 둔다)
 *    - `detail` 키 10개 고정: `kind` · `category` · `error_code` · `severity` · `module_id` · `execution_id` ·
 *      `command_id` · `context` · `reason` · `correlation_id`. 🔴 **`operation` 이 아니라 `kind`** 다 — 낱말은
 *      종전 `event_log.operation` 그대로다. `error_message` 는 이 원장에 없다
 * 🔴 **실물 대조**(dev2 2026-10-11 배치 `c7c362be` · 1차 4행 · 2차 13행): 앞 다섯(`progress_record` ×2 · `execution_terminal` ·
 *    `execution_observed` · `dispatch_send`)은 그 원문의 키 · 값 모양 그대로다(배치 id 만 fixture 것으로). 실물에서 본 것:
 *    - `execution_terminal`(실행마다 한 행 · 2차에 처음 나옴)은 `category: observation` · `context{via, step, total, reason,
 *      status, command_id}` — `reason` 은 모듈이 말한 종착 사유 문장이다
 *    - `progress_record` 는 10행 중 8행이 `said` 없이 장부 값(`handoff` · `point_id` · `status` …)만 싣는다
 *    - `execution_observed` 의 `category` 는 **`observation`** 이고 `module_id` 가 `detail` 칸과 `context` 둘 다에 있다
 *    - `dispatch_send` 는 `module_id` · `execution_id` · `command_id` 칸이 다 찬다
 *    - `progress_record` 는 문장이 있으면 `context.first_result.said` 에 싣는다(`module_id` · `execution_id` 칸은 null)
 *    ⚠ 뒤 둘(`batch_state_change` · `dispatch_halt`)은 **아직 실물에 나온 적이 없다**(3차까지 배치 둘 · 34행 0건) —
 *      서버 코드에서 지은 모양이다. 🔴 **서버가 적는 kind 는 맞다 — «시스템이 일으킨» 전이에서만 적힌다**:
 *      - `batch_state_change` 는 `batches._transition(system_cause=…)` 일 때만(`batches._record_state_change` 머리말
 *        「사람 경로(화면)는 넘기지 않아 행이 없다」). 원인 `cause.kind` = `dispatch_halt` · `auto_resume` ·
 *        `all_samples_settled` · `preflight_block`(`batches.CAUSE_*`). ⇒ **사람이 누른 시작 · 일시정지 · 재개 · 중지는 이
 *        원장에 없다** — 감사 원장(`event_type=audit` · `target batch:<id>`)에 남는다. 실물 `c7c362be` 가 aborted 로 끝났는데
 *        전이 행이 0인 까닭이 이것이다(사람이 중지했다)
 *      - `dispatch_halt` 는 발송이 스스로 멈춘 회차에만(`batch_dispatch._record_halt`) — 🔴 `error_code` 를 **비운다**
 *        (「알리는 몫은 알람」) · 문장은 `reason` · `module_id` · `execution_id` 를 함께 적는다. 같은 멈춤의 전이 행이 짝으로 선다
 *      ⚠ 2026-10-11 까지 이 fixture 는 `dispatch_halt` 에 `error_code: 'LOAD_OVERDUE'` 를, 전이 행에 `created → running`(사람 시작)을
 *        싣고 있었다 — **둘 다 서버가 만들지 않는 모양**이라 고쳤다
 */
export const BATCH_RECORD_HISTORY: HistoryEntry[] = [
  {
    ts: '2026-09-11T14:19:37+09:00',
    event_type: 'batch_record',
    target: 'batch:B-260911-02',
    detail: {
      kind: 'progress_record',
      reason: null,
      context: {
        command_id: '928b4952-e958-5c4c-8d9a-625d7c89c0d9',
        status_code: 200,
        first_result: { status: 'complete', execution_id: 'exec-41a752e5b42d' },
      },
      category: 'command',
      severity: 'INFO',
      module_id: null,
      command_id: '928b4952-e958-5c4c-8d9a-625d7c89c0d9',
      error_code: null,
      execution_id: null,
      correlation_id: null,
    },
  },
  {
    ts: '2026-09-11T14:19:36+09:00',
    event_type: 'batch_record',
    target: 'batch:B-260911-02',
    detail: {
      kind: 'execution_terminal',
      reason: null,
      context: {
        via: 'stream',
        step: 177,
        total: 177,
        reason: '실행 완료 — 스텝 177개',
        status: 'complete',
        command_id: 'd842f21e-8e84-5f19-a323-ab66087e9f7f',
      },
      category: 'observation',
      severity: 'INFO',
      module_id: 'centrifuge-1',
      command_id: 'd842f21e-8e84-5f19-a323-ab66087e9f7f',
      error_code: null,
      execution_id: 'exec-41a752e5b42d',
      correlation_id: null,
    },
  },
  {
    ts: '2026-09-11T14:18:06+09:00',
    event_type: 'batch_record',
    target: 'batch:B-260911-02',
    detail: {
      kind: 'progress_record',
      reason: null,
      context: {
        command_id: 'd842f21e-8e84-5f19-a323-ab66087e9f7f',
        status_code: 202,
        first_result: {
          said: '보냈고 모듈이 받았다',
          batch_id: 'B-260911-02',
          execution_id: 'exec-41a752e5b42d',
        },
      },
      category: 'command',
      severity: 'INFO',
      module_id: null,
      command_id: 'd842f21e-8e84-5f19-a323-ab66087e9f7f',
      error_code: null,
      execution_id: null,
      correlation_id: null,
    },
  },
  {
    ts: '2026-09-11T14:18:05.500+09:00',
    event_type: 'batch_record',
    target: 'batch:B-260911-02',
    detail: {
      kind: 'execution_observed',
      reason: null,
      context: { observed: 'module_execution', module_id: 'centrifuge-1' },
      category: 'observation',
      severity: 'INFO',
      module_id: 'centrifuge-1',
      command_id: null,
      error_code: null,
      execution_id: 'exec-41a752e5b42d',
      correlation_id: null,
    },
  },
  {
    ts: '2026-09-11T14:18:05.400+09:00',
    event_type: 'batch_record',
    target: 'batch:B-260911-02',
    detail: {
      kind: 'dispatch_send',
      reason: null,
      context: {
        items: 177,
        command_id: 'd842f21e-8e84-5f19-a323-ab66087e9f7f',
        stub_bases: { z_stub: 80, grip_stub: 20 },
        rules_undispatched: ['supply', 'batch_start'],
      },
      category: 'command',
      severity: 'INFO',
      module_id: 'centrifuge-1',
      command_id: 'd842f21e-8e84-5f19-a323-ab66087e9f7f',
      error_code: null,
      execution_id: 'exec-41a752e5b42d',
      correlation_id: null,
    },
  },
  {
    ts: '2026-09-11T14:09:21+09:00',
    event_type: 'batch_record',
    target: 'batch:B-260911-02',
    detail: {
      kind: 'batch_state_change',
      reason: null,
      context: {
        from: 'running',
        to: 'paused',
        cause: { kind: 'dispatch_halt', said: '적재 확인이 늦어 멈췄습니다' },
        batch_version: 3,
      },
      category: 'lifecycle',
      severity: 'INFO',
      module_id: null,
      command_id: null,
      error_code: null,
      execution_id: null,
      correlation_id: null,
    },
  },
  {
    ts: '2026-09-11T14:09:20+09:00',
    event_type: 'batch_record',
    target: 'batch:B-260911-02',
    detail: {
      kind: 'dispatch_halt',
      reason: '적재 확인이 늦어 멈췄습니다',
      context: null,
      category: 'lifecycle',
      severity: 'INFO',
      module_id: 'centrifuge-1',
      command_id: null,
      error_code: null,
      execution_id: 'exec-41a752e5b42d',
      correlation_id: null,
    },
  },
]

/**
 * 명령 원장 — `GET /history?event_type=command`.
 *
 * 🔴 **`target` 에 접두어가 «없다».** 세 원장 중 이 원장만 `command_log.target` 원본을 그대로
 *    싣는다(`app/services/history.py` 머리말 — 「세 원장의 `target` 꼴이 서로 다르다」).
 *    ⇒ **이 원장으로는 배치를 고를 수 없다.** `batch_id` 열은 표에 있지만 응답에 안 실린다.
 * 🔴 **`log_level` 을 채우지 마라.** 쓰는 코드가 0건이라 실물은 **언제나 `null`** 이다
 *    (`app/services/command_log.py` 머리말 — 「그 등급을 기계 판독으로 정해 둔 자리가 없다」).
 *    fixture 가 값을 넣으면 목에서만 「종류」 열이 차고 실물에서는 빈다.
 * ⚠ `actor` 는 이 원장에서 **항상 `null`** 이다(`sa.null().label("actor")`).
 */
export const COMMAND_HISTORY: HistoryEntry[] = [
  {
    ts: '2026-09-11T14:18:02+09:00',
    event_type: 'command',
    actor: null,
    target: 'cent',
    detail: {
      operation: 'run_centrifugation',
      result: 'dispatched',
      log_level: null,
    },
  },
]

/**
 * 감사 원장 — 누가 · 언제 · 무엇을 바꿨나. 해시체인으로 이어져 수정 불가.
 * `target` 은 `<자원>:<식별자>` 꼴이다.
 */
export const AUDIT_HISTORY: HistoryEntry[] = [
  {
    ts: '2026-09-11T14:31:59+09:00',
    event_type: 'audit',
    actor: 'system',
    target: 'module:cent',
    detail: { action: 'safety_stop', operation: '안전정지 (하드와이어)' },
  },
  {
    ts: '2026-09-11T14:31:20+09:00',
    event_type: 'audit',
    actor: 'hyungwoo.cho',
    target: 'alarm:LOAD_OVERDUE',
    detail: { action: 'ack' },
  },
  /*
   * 🔴 **배치 명령은 전부 이 원장에 남는다** — `app/views/batches.py` `_write_audit` 가
   *    `target_resource="batch"` 로 적고 `audit.compose` 가 `batch:<id>` 를 만든다.
   *    작업 상세 「명령」 탭이 보는 것이 이 행들이다.
   * ⚠ `operation` · `reason` 은 **DB CHECK 로 모든 행에 필수**다. 🔴 `detail` 에 `command_id` 키는 **없다** —
   *   사람 사유가 없는 명령은 서버가 `synthesize_reason`(`app/services/users.py`)으로 채우고 `command_id` 는 그 문자열 안에만
   *   있다(`operation=… target=… actor=… command_id=… changed=none`). 전이 명령은 `from_status`(요청 때 상태)를 더 싣는다
   *   (`app/views/batches.py` · `batches.transition_audit`). ⚠ 2026-10-11 까지 이 두 행은 `detail.command_id` 를 싣고
   *   합성 사유에 `command_id=` · `changed=` 조각이 없었다 — 서버가 만들지 않는 모양이라 고쳤다(그 꼴이면 합성 판정에
   *   안 걸려 기계 문자열이 «사람 사유»로 읽힌다).
   * ⚠ 실물 `actor` 는 **UUID** 다(2026-09-20 실측). 여기 로그인 ID 를 쓴 것은 사용자 관리
   *   화면이 두 축(`user_id` · `username`)을 다 받게 되어 있어서다(`matchesUser` 주석).
   */
  {
    ts: '2026-09-11T14:18:02+09:00',
    event_type: 'audit',
    actor: 'hyungwoo.cho',
    target: 'batch:B-260911-02',
    detail: {
      action: 'batch_control',
      operation: 'startBatch',
      reason:
        'operation=startBatch target=B-260911-02 actor=hyungwoo.cho command_id=6ccd24ac-f953-41a6-9c03-ac4a1e61a4b5 changed=none',
      from_status: 'created',
    },
  },
  {
    ts: '2026-09-11T14:10:00+09:00',
    event_type: 'audit',
    actor: 'hyungwoo.cho',
    target: 'batch:B-260911-02',
    detail: {
      action: 'batch_control',
      operation: 'createBatch',
      reason:
        'operation=createBatch target=B-260911-02 actor=hyungwoo.cho command_id=f92c62a5-b35f-47c8-92fd-79e32308416e changed=none',
    },
  },
  {
    ts: '2026-09-11T13:02:11+09:00',
    event_type: 'audit',
    actor: 'eric.cho',
    target: 'recipe:rcp-icc-std',
    detail: {
      action: 'recipe_edit',
      before: { speed_rpm: 3000 },
      after: { speed_rpm: 1021 },
      reason: 'RCF 상한 반영',
    },
  },
  {
    ts: '2026-09-11T09:15:40+09:00',
    event_type: 'audit',
    actor: 'eric.cho',
    target: 'user:hyungwoo.cho',
    detail: {
      action: 'role_change',
      before: { role: 'viewer' },
      after: { role: 'operator' },
      reason: '신규 작업자',
    },
  },
  {
    ts: '2026-09-10T17:20:00+09:00',
    event_type: 'audit',
    actor: 'eric.cho',
    target: 'user:minji',
    detail: { action: 'user_create', after: { role: 'viewer' }, reason: '병원 모니터링' },
  },
  {
    ts: '2026-09-09T15:40:00+09:00',
    event_type: 'audit',
    actor: 'seojun',
    target: 'ledger:all',
    detail: { action: 'chain_verify', result: '정상', row_count: 11870 },
  },
]

/** 네 원장 합성 뷰. `GET /history` 가 그대로 이 꼴로 준다(`ts` 내림차순) */
export const HISTORY: HistoryEntry[] = [
  ...EVENT_HISTORY,
  ...BATCH_RECORD_HISTORY,
  ...AUDIT_HISTORY,
  ...COMMAND_HISTORY,
].sort((a, b) => b.ts.localeCompare(a.ts))

/**
 * 시스템 로그(`GET /logs`).
 * `source` 는 `"{node}/{component}"` 로 합성한 값이다 — `app_log` 에 동명 컬럼이 없다.
 */
export const APP_LOGS: LogEntry[] = [
  {
    ts: '2026-09-11T14:20:03+09:00',
    level: 'info',
    source: 'l2.orchestrator/scheduler',
    message: 'step_complete · inspect · rotor_indexed · B-260911-02',
    context: { correlation_id: 'corr_2b7d' },
  },
  {
    ts: '2026-09-11T14:19:40+09:00',
    level: 'warn',
    source: 'l3.robot/driver.ur',
    message: 'COMM_TIMEOUT · 재시도 2/3 · transient',
    context: { correlation_id: 'corr_4c1e' },
  },
  {
    ts: '2026-09-11T14:19:12+09:00',
    level: 'info',
    source: 'l2.gateway/auth',
    message: '세션 갱신 · eric.cho · 데스크톱',
  },
  {
    ts: '2026-09-11T14:18:02+09:00',
    level: 'info',
    source: 'l2.orchestrator/scheduler',
    message: '작업 시작 · B-260911-02 · 사전 점검 통과',
  },
  {
    // 🔴 시료 ID 가 메시지 본문에 섞여 있다 — `view` 권한 없는 역할에게는 마스킹 대상이다
    ts: '2026-09-11T14:17:55+09:00',
    level: 'debug',
    source: 'l3.centrifuge/driver.sigma',
    message: 'rotor index 1→2 · S-1045 · S-1046',
  },
  {
    ts: '2026-09-11T14:12:30+09:00',
    level: 'error',
    source: 'l2.db/writer',
    message: 'DB_WRITE_FAIL · 감사 삽입 재시도 1/3 · 복구됨',
  },
  {
    ts: '2026-09-11T14:05:00+09:00',
    level: 'info',
    source: 'l2.orchestrator/discovery',
    message: 'mDNS 재발견 · 어셈블러 10.20.0.12',
  },
]
